/**
 * JAISIU-4447 — per-attachment playback position bookkeeping.
 *
 * ## Why this exists
 *
 * The overlay no longer renders the native `<audio controls>` /
 * `<video controls>` strips (JAISIU-4447), so the custom progress
 * bar is the ONLY scrubber the operator has. Two consequences:
 *
 *   1. **A seek must stick.** While a seek is in flight the browser
 *      keeps reporting the OLD `currentTime` and fires `timeupdate`
 *      / `seeked` events. The bar must hold the requested position
 *      until the media lands, otherwise the playhead visibly snaps
 *      back.
 *   2. **The position must survive a re-open.** The operator can
 *      close the preview and open the same attachment again; the
 *      player should come back where they left it.
 *
 * The registry is keyed by attachment id so two previews of the same
 * attachment (transcript chip + overlay) share one position.
 *
 * The bookkeeping itself is pure and lives in
 * {@link ./media-seek-state.ts}; this module is the thin, mutable
 * store that the overlay talks to.
 */

import {
  createSeekState,
  reconcileSeek,
  requestSeek,
  type PendingSeekState,
} from "./media-seek-state.ts";

interface AttachmentPlaybackState {
  /** Seek bookkeeping for the in-flight / last seek. */
  seek: PendingSeekState;
  /** Last known settled playhead position (seconds). */
  position: number;
  /** Whether the operator already interacted with this preview. */
  touched: boolean;
}

const states = new Map<string, AttachmentPlaybackState>();

/**
 * Read (or lazily create) the state for an attachment.
 */
function stateFor(attachmentId: string): AttachmentPlaybackState {
  let state = states.get(attachmentId);
  if (!state) {
    state = {
      seek: createSeekState(),
      position: 0,
      touched: false,
    };
    states.set(attachmentId, state);
  }
  return state;
}

/**
 * Record a seek request for an attachment.
 *
 * The requested position is remembered as PENDING: while the browser
 * has not landed the seek yet, {@link resolveAttachmentPlaybackTime}
 * keeps returning the requested position so stale media events cannot
 * overwrite it.
 */
export function requestAttachmentSeek(
  attachmentId: string,
  seconds: number,
): void {
  if (!attachmentId) return;
  const state = stateFor(attachmentId);
  state.seek = requestSeek(state.seek, seconds);
  state.touched = true;
}

/**
 * Reconcile the pending seek against the media element's reported
 * position.
 *
 * Returns the position the progress bar should render:
 *
 *   - the PENDING target while the seek has not landed (stale
 *     `timeupdate` / `seeked` events cannot move the playhead back);
 *   - the media's own position once the seek has completed.
 */
export function resolveAttachmentPlaybackTime(
  attachmentId: string,
  currentTime: number,
): number {
  if (!attachmentId) {
    return Number.isFinite(currentTime) ? currentTime : 0;
  }
  const state = stateFor(attachmentId);
  const reconciled = reconcileSeek(state.seek, currentTime);
  state.seek = reconciled.state;
  state.position = reconciled.time;
  return reconciled.time;
}

/**
 * The last known playhead position for an attachment (`0` when the
 * attachment was never played).
 */
export function attachmentPlaybackPosition(attachmentId: string): number {
  if (!attachmentId) return 0;
  return stateFor(attachmentId).position;
}

/**
 * Forget an attachment's playback state (used when the preview is
 * closed for good, and by tests).
 */
export function resetAttachmentPlaybackState(attachmentId?: string): void {
  if (attachmentId) {
    states.delete(attachmentId);
    return;
  }
  states.clear();
}
